import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  LayoutDashboard, TrendingUp, TrendingDown, Wallet, CreditCard,
  Target, Briefcase, Bell, ChevronLeft, ChevronRight, Settings,
  UserCog, Key, ChevronDown
} from "lucide-react";
import logo from '../Images/logo.jpg';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // Yeh redirection logic zaroori hai aur ismein koi badlav nahi hoga
  /* useEffect(() => {
    if (user && !user.isProfileComplete && location.pathname !== '/CompleteProfile') {
      navigate('/CompleteProfile', { replace: true });
    }
  }, [user, location.pathname, navigate]); */

  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isSettingsSubmenuOpen, setIsSettingsSubmenuOpen] = useState(false);
  const navRef = useRef<HTMLDivElement>(null);
  const activeItemRef = useRef<HTMLAnchorElement>(null);

  const colorClasses = useMemo(() => ({
    blue: (active: boolean) => active ? 'text-blue-600 bg-blue-100 border-blue-500' : 'text-gray-600 hover:text-blue-600 hover:bg-blue-50',
    emerald: (active: boolean) => active ? 'text-emerald-600 bg-emerald-100 border-emerald-500' : 'text-gray-600 hover:text-emerald-600 hover:bg-emerald-50',
    red: (active: boolean) => active ? 'text-red-600 bg-red-100 border-red-500' : 'text-gray-600 hover:text-red-600 hover:bg-red-50',
    purple: (active: boolean) => active ? 'text-purple-600 bg-purple-100 border-purple-500' : 'text-gray-600 hover:text-purple-600 hover:bg-purple-50',
    orange: (active: boolean) => active ? 'text-orange-600 bg-orange-100 border-orange-500' : 'text-gray-600 hover:text-orange-600 hover:bg-orange-50',
    indigo: (active: boolean) => active ? 'text-indigo-600 bg-indigo-100 border-indigo-500' : 'text-gray-600 hover:text-indigo-600 hover:bg-indigo-50',
    cyan: (active: boolean) => active ? 'text-cyan-600 bg-cyan-100 border-cyan-500' : 'text-gray-600 hover:text-cyan-600 hover:bg-cyan-50',
    pink: (active: boolean) => active ? 'text-pink-600 bg-pink-100 border-pink-500' : 'text-gray-600 hover:text-pink-600 hover:bg-pink-50'
  }), []);

  const handleResize = useCallback(() => {
    const timeout = setTimeout(() => { setIsMobile(window.innerWidth < 768); }, 100);
    return () => clearTimeout(timeout);
  }, []);

  useEffect(() => {
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, [handleResize]);

  useEffect(() => {
    if (activeItemRef.current && navRef.current) {
      const nav = navRef.current;
      const activeItem = activeItemRef.current;
      const itemOffsetLeft = activeItem.offsetLeft;
      const itemWidth = activeItem.offsetWidth;
      const navWidth = nav.offsetWidth;
      nav.scrollTo({ left: itemOffsetLeft - (navWidth / 2) + (itemWidth / 2), behavior: 'smooth' });
      setTimeout(() => setScrollPosition(nav.scrollLeft), 300);
    }
  }, [location.pathname]);

  const updateScrollPosition = useCallback(() => {
    if (navRef.current) { setScrollPosition(navRef.current.scrollLeft); }
  }, []);

  const scrollNav = useCallback((direction: 'left' | 'right') => {
    if (navRef.current) {
      const scrollAmount = 200;
      navRef.current.scrollBy({ left: direction === 'right' ? scrollAmount : -scrollAmount, behavior: 'smooth' });
      setTimeout(updateScrollPosition, 300);
    }
  }, [updateScrollPosition]);

  const handleLogout = useCallback(() => {
    logout();
    setIsDropdownOpen(false);
    setIsSettingsSubmenuOpen(false);
  }, [logout]);

  const toggleDropdown = useCallback(() => {
    setIsDropdownOpen(prev => {
      if (prev) setIsSettingsSubmenuOpen(false);
      return !prev;
    });
  }, []);

  const toggleSettingsSubmenu = useCallback(() => {
    setIsSettingsSubmenuOpen(prev => !prev);
  }, []);

  const isActive = useCallback((path: string) => location.pathname === path, [location.pathname]);

  const getPageColorTheme = useCallback(() => {
    const path = location.pathname.toLowerCase();
    if (path === '/completeprofile') return 'blue';
    if (path === '/overview') return 'blue';
    if (path === '/profile') return 'indigo';
    if (path === '/assets') return 'emerald';
    if (path === '/liability') return 'red';
    if (path === '/income') return 'blue';
    if (path === '/expenses') return 'orange';
    if (path === '/insurance') return 'pink';
    if (path === '/goals') return 'purple';
    if (path.startsWith('/settings')) return 'cyan';
    return 'blue';
  }, [location.pathname]);

  const canScrollLeft = scrollPosition > 0;
  const canScrollRight = navRef.current ? scrollPosition < (navRef.current.scrollWidth - navRef.current.clientWidth - 10) : false;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-indigo-50 flex flex-col">
      <header className="bg-white/90 backdrop-blur-lg border-b border-white/20 sticky top-0 z-50">
        <div className="flex items-center justify-between py-3 px-4 md:px-6">
          <div className="flex items-center">
            <img src={logo} alt="Logo" className="h-8 w-8 mr-3 rounded-full"/>
            <h1 className="text-xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
              MYW Plan
            </h1>
          </div>
          <div className="flex items-center space-x-4">
            <button className="p-2 rounded-full hover:bg-blue-100/50 transition-colors">
              <Bell className="h-5 w-5 text-gray-600" />
            </button>
            {user && (
              <div className="relative">
                <button onClick={toggleDropdown} className="flex items-center space-x-3 p-2 rounded-lg hover:bg-blue-100/50 transition-colors">
                  <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-full h-8 w-8 flex items-center justify-center text-sm font-semibold text-white">
                    {user.name?.charAt(0) || 'U'}
                  </div>
                  <div className="hidden md:block">
                    <p className="text-sm font-medium text-gray-900">{user.name}</p>
                  </div>
                </button>
                {isDropdownOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white/90 backdrop-blur-lg rounded-xl shadow-2xl py-1 z-50 border border-white/20">
                    <div className="relative">
                      <button onClick={toggleSettingsSubmenu} className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-blue-100/50 transition-colors">
                        <Settings className="h-4 w-4 mr-2" />
                        Settings
                        <ChevronDown className={`h-4 w-4 ml-auto transition-transform ${isSettingsSubmenuOpen ? 'rotate-180' : ''}`} />
                      </button>
                      {isSettingsSubmenuOpen && (
                        <div className="pl-6 bg-blue-50/50 border-l border-blue-200/50">
                          <Link to="/settings" className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-blue-100/50 transition-colors" onClick={() => setIsDropdownOpen(false)}>
                            <Settings className="h-4 w-4 mr-2" />
                            General Settings
                          </Link>
                          <Link to="/settings#change-password" className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-blue-100/50 transition-colors" onClick={() => setIsDropdownOpen(false)}>
                            <Key className="h-4 w-4 mr-2" />
                            Change Password
                          </Link>
                        </div>
                      )}
                    </div>
                    <Link to="/profile" className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-blue-100/50 transition-colors" onClick={() => setIsDropdownOpen(false)}>
                      <UserCog className="h-4 w-4 mr-2" />
                      Update Profile
                    </Link>
                    <button onClick={handleLogout} className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-100/50 transition-colors">
                      <span>Logout</span>
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        
        {/* --- V V V --- BADLAV YAHAN HAI: Navbar ab hamesha dikhega --- V V V --- */}
        <div className="bg-white/90 border-t border-white/20 relative">
          {isMobile && canScrollLeft && (
            <button onClick={() => scrollNav('left')} className="absolute left-0 top-1/2 transform -translate-y-1/2 z-10 bg-white/90 shadow-md rounded-full p-1 ml-1">
              <ChevronLeft className="h-5 w-5 text-gray-600" />
            </button>
          )}
          {isMobile && canScrollRight && (
            <button onClick={() => scrollNav('right')} className="absolute right-0 top-1/2 transform -translate-y-1/2 z-10 bg-white/90 shadow-md rounded-full p-1 mr-1">
              <ChevronRight className="h-5 w-5 text-gray-600" />
            </button>
          )}
          <div ref={navRef} className="overflow-x-auto hide-scrollbar px-2 md:px-6 py-2" onScroll={updateScrollPosition}>
            <nav className="flex justify-center space-x-1 min-w-max">
              {[
                { path: "/Overview", name: "Overview", icon: <LayoutDashboard className="h-4 w-4 md:h-5 md:w-5" /> },
                { path: "/assets", name: "Assets", icon: <TrendingUp className="h-4 w-4 md:h-5 md:w-5" /> },
                { path: "/liability", name: "Liability", icon: <TrendingDown className="h-4 w-4 md:h-5 md:w-5" /> },
                { path: "/income", name: "Income", icon: <Wallet className="h-4 w-4 md:h-5 md:w-5" /> },
                { path: "/expenses", name: "Expenses", icon: <CreditCard className="h-4 w-4 md:h-5 md-w-5" /> },
                { path: "/insurance", name: "Insurance", icon: <Briefcase className="h-4 w-4 md:h-5 md:w-5" /> },
                { path: "/goals", name: "Goals", icon: <Target className="h-4 w-4 md:h-5 md:w-5" /> },
              ].map((item) => {
                const active = isActive(item.path);
                const colorTheme = getPageColorTheme();
                
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    ref={active ? activeItemRef : null}
                    className={`flex items-center py-2 px-3 md:py-2 md:px-4 transition-all duration-200 whitespace-nowrap rounded-xl border-2 ${
                      active ? `${colorClasses[colorTheme](true)} font-medium transform scale-105 shadow-md -translate-y-0.5 z-10` 
                             : `${colorClasses.blue(false)} border-transparent hover:border-blue-200/50`
                    }`}
                  >
                    {item.icon}
                    <span className="ml-2 text-xs md:text-sm">{item.name}</span>
                  </Link>
                );
              })}
            </nav>
          </div>
        </div>
        {/* --- ^ ^ ^ --- END OF CHANGE --- ^ ^ ^ --- */}
      </header>

      <div className="flex-1 overflow-auto">
        <main className="p-2 md:p-6">
          {children}
        </main>
      </div>

      <style>{`
        .hide-scrollbar::-webkit-scrollbar { display: none; }
        .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>
    </div>
  );
};

export default Layout;